import { Col, FormGroup, Row, Text } from '@ksyos/design-system';
import type { ITooProcedureCustomControlData } from '@common/validation/customControls/ophthalmology';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { CustomControlMode, FilesUrlBase, ServiceCode, ServiceType } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { booleanAsJaNee } from '@common/utils/textUtils';
import { OphthalmologyFilePlacement, TooRedenBezoek } from '@common/validation/customControls/ophthalmology';
import { ProcedureReadButtons } from '@frontend/components/elements/carePathButtons';
import { FormLabel, HorizontalFormGroupRead, InputContainer } from '@frontend/components/elements/forms';
import { getFilesByPlacement } from '@frontend/components/pages/customControls/TFO';
import { PatientInsuranceOldCarepath } from '@frontend/components/pages/episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import { translateKey } from '@frontend/translate';
import { useEpisodeContext } from '@frontend/utils';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import {
    FormDivider,
    FormParagraph,
    HeaderDivider,
    StyledBorderAndPadding,
    UserTextParagraph,
} from '../../../../elements/common';
import { ProcessContainer, RenderAssetThumbnails } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { OphthalmologyDropzoneRead } from '../../TFO/v1/OphthalmologyDropzoneRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { TooV1ProcedureReadHeading } from './TooV1ProcedureReadComponents/TooV1ProcedureReadHeading';
import { TooV1ProcedureReadOdOs } from './TooV1ProcedureReadComponents/TooV1ProcedureReadOdOs';
import { TooV1ProcedureReadOdOsLabel } from './TooV1ProcedureReadComponents/TooV1ProcedureReadOdOsLabel';
import { TooV1ProcedureReadSphCylAx } from './TooV1ProcedureReadComponents/TooV1ProcedureReadSphCylAx';

type TooV1ProcedureReadProps = Readonly<
    ICustomControlProcedureReadProps & {
        isReferralCheckCompleted?: boolean;
    }
>;

export function TooV1ProcedureRead(props: TooV1ProcedureReadProps) {
    const { isReferralCheckCompleted } = props;
    const { patient } = useEpisodeContext();

    const customControlData: ITooProcedureCustomControlData = props.procedure.customControlData;
    const eigenCorrectie =
        customControlData.eigen_correctie_sferisch_od ||
        customControlData.eigen_correctie_sferisch_os ||
        customControlData.eigen_correctie_cylindrish_od ||
        customControlData.eigen_correctie_cylindrish_os ||
        customControlData.eigen_correctie_as_od ||
        customControlData.eigen_correctie_as_os;

    const autorefractie =
        customControlData.autorefractie_sferisch_od ||
        customControlData.autorefractie_sferisch_os ||
        customControlData.autorefractie_cylindrisch_od ||
        customControlData.autorefractie_cylindrisch_os ||
        customControlData.autorefractie_as_od ||
        customControlData.autorefractie_as_os;
    const nabijeVisus = customControlData.nabije_visus_od || customControlData.nabije_visus_os;
    const breakUpTime = customControlData.break_up_time_od || customControlData.break_up_time_os;
    const amslerTest = customControlData.amsler_test_od || customControlData.amsler_test_os;
    const cyclopegishe =
        customControlData.cycloplegische_sferisch_od ||
        customControlData.cycloplegische_sferisch_os ||
        customControlData.cycloplegische_cylindrisch_od ||
        customControlData.cycloplegische_cylindrisch_os ||
        customControlData.cycloplegische_as_od ||
        customControlData.cycloplegische_as_os;
    const pachymetrie = customControlData.pachymetrie_od || customControlData.pachymetrie_os;
    const gonioscopie =
        customControlData.gonioscopie_od ||
        customControlData.gonioscopie_os ||
        customControlData.gonioscopie_toelichting_od ||
        customControlData.gonioscopie_toelichting_os;

    const extraExaminations =
        eigenCorrectie ||
        autorefractie ||
        nabijeVisus ||
        breakUpTime ||
        amslerTest ||
        cyclopegishe ||
        pachymetrie ||
        gonioscopie;

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={ServiceType.examination}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            <PatientInsuranceOldCarepath
                isPatientInformed={props.procedure.customControlData.isPatientInformed}
                mode={CustomControlMode.read}
                serviceCode={ServiceCode.TOO}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <>
                    {props.request.draftCareProviderPractitioner && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Huisarts</FormLabel>
                            <InputContainer>
                                <FormParagraph>
                                    {getDisplayName(props.request.draftCareProviderPractitioner)}
                                </FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                    {customControlData.verwijsbrief_beschikbaar !== null && (
                        <HorizontalFormGroupRead>
                            <FormLabel>De patiënt heeft een verwijzing van de huisarts</FormLabel>
                            <InputContainer>
                                <FormParagraph>
                                    {booleanAsJaNee(customControlData.verwijsbrief_beschikbaar)}
                                </FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                    {customControlData.huisarts_is_betrokken !== null && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Huisarts is betrokken</FormLabel>
                            <InputContainer>
                                <FormParagraph>{booleanAsJaNee(customControlData.huisarts_is_betrokken)}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                    {customControlData.huisarts_betrokkenheid && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Betrokkenheid van huisarts</FormLabel>
                            <InputContainer>
                                <FormParagraph>{customControlData.huisarts_betrokkenheid}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                    {(customControlData.huisarts_betrokkenheid !== null ||
                        customControlData.verwijsbrief_beschikbaar) &&
                        customControlData.files.length > 0 && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Verwijsbrief</FormLabel>
                                <RenderAssetThumbnails
                                    assets={getFilesByPlacement(
                                        customControlData.files,
                                        OphthalmologyFilePlacement.verwijsbrief
                                    )}
                                    id={props.procedure.id}
                                    urlBase={FilesUrlBase.procedures}
                                />
                            </HorizontalFormGroupRead>
                        )}

                    {(isReferralCheckCompleted === undefined || isReferralCheckCompleted) && (
                        <>
                            <HeaderDivider text="Protocol" />
                            {customControlData.protocol && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Protocol</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>{customControlData.protocol}</FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            <HeaderDivider text="Anamnese" />
                            {customControlData.reden_van_bezoek && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Reden van bezoek</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            {customControlData.reden_van_bezoek !== TooRedenBezoek.anders
                                                ? customControlData.reden_van_bezoek
                                                : customControlData.reden_van_bezoek_overig}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            {customControlData.klachten && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Klachten</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>{customControlData.klachten}</FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            {customControlData.oculaire_voorgeschiedenis && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Oculaire voorgeschiedenis</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            {customControlData.oculaire_voorgeschiedenis}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            {customControlData.familiaire_voorgeschiedenis && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Familiaire voorgeschiedenis</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            {customControlData.familiaire_voorgeschiedenis}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}
                            {customControlData.algemene_gezondheid_en_medicatie && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Algemene gezondheid en medicatie</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            {customControlData.algemene_gezondheid_en_medicatie}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}
                            {customControlData.rijdt_auto !== null && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Rijdt auto?</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            {customControlData.rijdt_auto ? 'Ja' : 'Nee'}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}
                            {customControlData.ervaart_klachten_als_storend !== null && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Ervaart klachten als storend?</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            {customControlData.ervaart_klachten_als_storend ? 'Ja' : 'Nee'}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            <FormDivider />
                            <HeaderDivider text="Onderzoek" />

                            <OphthalmologyDropzoneRead
                                files={props.procedure.customControlData.files}
                                id={props.procedure.id}
                            />

                            {customControlData.draagt_correctie !== null && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>Draagt correctie</FormLabel>
                                    <InputContainer>
                                        <UserTextParagraph>
                                            {customControlData.draagt_correctie ? 'Ja' : 'Nee'}
                                        </UserTextParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            {(customControlData.visus_od || customControlData.visus_os) && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.visus_od}
                                    dataOs={customControlData.visus_os}
                                    label="Visus zonder of eigen correctie"
                                />
                            )}
                            {(customControlData.refractie_sferisch_od ||
                                customControlData.refractie_sferisch_os ||
                                customControlData.refractie_cylindrish_od ||
                                customControlData.refractie_cylindrish_os ||
                                customControlData.refractie_as_od ||
                                customControlData.refractie_as_os) && (
                                <TooV1ProcedureReadSphCylAx
                                    data={{
                                        as_od: customControlData.refractie_as_od,
                                        as_os: customControlData.refractie_as_os,
                                        cylindrisch_od: customControlData.refractie_cylindrish_od,
                                        cylindrisch_os: customControlData.refractie_cylindrish_os,
                                        sferisch_od: customControlData.refractie_sferisch_od,
                                        sferisch_os: customControlData.refractie_sferisch_os,
                                    }}
                                    label="Refractie"
                                />
                            )}

                            {(customControlData.visus_refractie_od || customControlData.visus_refractie_os) && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.visus_refractie_od}
                                    dataOs={customControlData.visus_refractie_os}
                                    label="Visus na refractie"
                                />
                            )}
                            {(customControlData.oogdruk_od || customControlData.stenopeische_visus_os) && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.stenopeische_visus_od}
                                    dataOs={customControlData.stenopeische_visus_os}
                                    label="Stenopeïsche visus"
                                />
                            )}
                            {(customControlData.oogdruk_od || customControlData.oogdruk_os) && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.oogdruk_od}
                                    dataOs={customControlData.oogdruk_os}
                                    label="Oogdruk (applanatie tonometrie)"
                                    units="mmHg"
                                />
                            )}

                            <TooV1ProcedureReadHeading label="Toegevoegd(e) onderzoek(en)" />

                            {extraExaminations && eigenCorrectie && (
                                <TooV1ProcedureReadSphCylAx
                                    data={{
                                        as_od: customControlData.eigen_correctie_as_od,
                                        as_os: customControlData.eigen_correctie_as_os,
                                        cylindrisch_od: customControlData.eigen_correctie_cylindrish_od,
                                        cylindrisch_os: customControlData.eigen_correctie_cylindrish_os,
                                        sferisch_od: customControlData.eigen_correctie_sferisch_od,
                                        sferisch_os: customControlData.eigen_correctie_sferisch_os,
                                    }}
                                    label="Eigen correctie"
                                />
                            )}

                            {autorefractie && (
                                <TooV1ProcedureReadSphCylAx
                                    data={{
                                        as_od: customControlData.autorefractie_as_od,
                                        as_os: customControlData.autorefractie_as_os,
                                        cylindrisch_od: customControlData.autorefractie_cylindrisch_od,
                                        cylindrisch_os: customControlData.autorefractie_cylindrisch_os,
                                        sferisch_od: customControlData.autorefractie_sferisch_od,
                                        sferisch_os: customControlData.autorefractie_sferisch_os,
                                    }}
                                    label="Autorefractie"
                                />
                            )}

                            {nabijeVisus && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.nabije_visus_od}
                                    dataOs={customControlData.nabije_visus_os}
                                    label="Nabij visus"
                                />
                            )}
                            {breakUpTime && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.break_up_time_od}
                                    dataOs={customControlData.break_up_time_os}
                                    label="Break-up time (BUT) test"
                                    units="sec."
                                />
                            )}
                            {amslerTest && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.amsler_test_od}
                                    dataOs={customControlData.amsler_test_os}
                                    label="Amsler test"
                                />
                            )}
                            {cyclopegishe && (
                                <TooV1ProcedureReadSphCylAx
                                    data={{
                                        as_od: customControlData.cycloplegische_as_od,
                                        as_os: customControlData.cycloplegische_as_os,
                                        cylindrisch_od: customControlData.cycloplegische_cylindrisch_od,
                                        cylindrisch_os: customControlData.cycloplegische_cylindrisch_os,
                                        sferisch_od: customControlData.cycloplegische_sferisch_od,
                                        sferisch_os: customControlData.cycloplegische_sferisch_os,
                                    }}
                                    label="Cycloplegische refractie"
                                />
                            )}
                            {pachymetrie && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.pachymetrie_od}
                                    dataOs={customControlData.pachymetrie_os}
                                    label="Pachymetrie"
                                    units="μm"
                                />
                            )}
                            {gonioscopie && (
                                <TooV1ProcedureReadOdOs
                                    dataOd={customControlData.gonioscopie_od}
                                    dataOs={customControlData.gonioscopie_os}
                                    label="Gonioscopie/ voorsegment OCT"
                                >
                                    <Text size="sm">{customControlData.gonioscopie_toelichting_od}</Text>
                                    <Text size="sm">{customControlData.gonioscopie_toelichting_os}</Text>
                                </TooV1ProcedureReadOdOs>
                            )}

                            {customControlData.aanvullend_onderzoek &&
                                customControlData.aanvullend_onderzoek
                                    .filter((onderzoek: string | null) => onderzoek !== null)
                                    .map((onderzoek: string, index: number) => (
                                        <FormGroup
                                            key={`additionalExamination${index}`}
                                            label={index === 0 ? 'Aanvullende onderzoeken' : ''}
                                        >
                                            <HorizontalFormGroupRead>
                                                <InputContainer>
                                                    <FormParagraph bold>{onderzoek}</FormParagraph>
                                                </InputContainer>
                                            </HorizontalFormGroupRead>

                                            {((customControlData.aanvullend_onderzoek_od &&
                                                customControlData.aanvullend_onderzoek_od[index]) ||
                                                (customControlData.aanvullend_onderzoek_os &&
                                                    customControlData.aanvullend_onderzoek_os[index])) && (
                                                <>
                                                    <Row columnGap={[2, 4, 6]}>
                                                        <Col span={[12, 6, null, null, 4]}>
                                                            <StyledBorderAndPadding>
                                                                <TooV1ProcedureReadOdOsLabel side="OD" />
                                                                <Text size="sm">
                                                                    {customControlData.aanvullend_onderzoek_od &&
                                                                        customControlData.aanvullend_onderzoek_od[
                                                                            index
                                                                        ]}
                                                                </Text>
                                                            </StyledBorderAndPadding>
                                                        </Col>
                                                        <Col span={[12, 6, null, null, 4]}>
                                                            <StyledBorderAndPadding>
                                                                <TooV1ProcedureReadOdOsLabel side="OS" />
                                                                <Text size="sm">
                                                                    {customControlData.aanvullend_onderzoek_os &&
                                                                        customControlData.aanvullend_onderzoek_os[
                                                                            index
                                                                        ]}
                                                                </Text>
                                                            </StyledBorderAndPadding>
                                                        </Col>
                                                    </Row>
                                                </>
                                            )}
                                        </FormGroup>
                                    ))}

                            {props.procedure.remark && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>{translateKey('diagnosticReport.remarks')}</FormLabel>
                                    <InputContainer>
                                        <UserTextParagraph>{props.procedure.remark}</UserTextParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                            <ProcedureReadButtons {...props} />
                        </>
                    )}
                </>
            </CustomControlCollapseBody>
            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('procedure.hideProcedure')}
                    labelMore={translateKey('procedure.showProcedure')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
